import React from "react";
import CustomModalV1 from "components/custom-modal/CustomModalV1";
import AuthForm from "components/auth-form/AuthForm";
import Button from "components/button/Button";
import { XIconV1 } from "images/image-component";
import LoadingModal from "modals/loading/LoadingModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import SuccessModal from "modals/success-modal/SuccessModal";
import { useNotificationEmail } from "./useNotificationEmail";
import "./notification-email-modal.css";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const TOTAL_STEPS = 3;

/**
 * Turn on transaction-receipt emails: confirm the password, then the address, then store it.
 *
 * No "try again" on the error modal: the form is still filled in behind it, and a modal-driven
 * resubmit would spend one of the three password attempts or one of the five server-side code
 * guesses without the user choosing to.
 */
function NotificationEmailModal({ isOpen, onClose }: Props) {
  const {
    step,
    title,
    subtitle,
    fields,
    submitRef,
    submitLabel,
    isSubmitDisabled,
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    showSuccess,
    successMsg,
    noticeMsg,
    canResend,
    resendLabel,
    endsSession,
    handleResend,
    handleChangeEmail,
    handleSubmit,
    handleCloseError,
    handleCloseSuccess,
    reset,
  } = useNotificationEmail();

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSuccessClose = () => {
    handleCloseSuccess();
    reset();
    onClose();
  };

  // The last wrong password ends the session on close, and reset() is what fires it — so that
  // close has to take the whole modal down, not just the error over it.
  const handleErrorClose = () => {
    if (endsSession) return handleClose();
    handleCloseError();
  };

  return (
    <>
      <CustomModalV1 isOpen={isOpen} handleClose={handleClose}>
        <button
          className="v1-modal-close"
          aria-label="Close"
          onClick={handleClose}
          disabled={showLoading}
        >
          <XIconV1 />
        </button>

        <div className="nem-header">
          <span className="nem-step">
            Step {step} of {TOTAL_STEPS}
          </span>
          <h2 className="v1-modal-title">{title}</h2>
          <p className="v1-modal-subtitle">{subtitle}</p>
        </div>

        <AuthForm
          fields={fields}
          submitLabel={submitLabel}
          onSubmit={handleSubmit}
          disabled={isSubmitDisabled}
          isLoading={showLoading}
          submitRef={submitRef}
        />

        {noticeMsg && <p className="nem-notice">{noticeMsg}</p>}

        {step === 3 && (
          <div className="nem-links">
            <button
              type="button"
              className="nem-link"
              onClick={handleResend}
              disabled={!canResend || showLoading}
            >
              {resendLabel}
            </button>
            <button
              type="button"
              className="nem-link"
              onClick={handleChangeEmail}
              disabled={showLoading}
            >
              Use a different address
            </button>
          </div>
        )}

        <div className="v1-modal-actions">
          <Button variant="ghost" onClick={handleClose} disabled={showLoading}>
            Cancel
          </Button>
        </div>
      </CustomModalV1>

      <LoadingModal isOpen={showLoading} loadingMsg={loadingMsg} />

      <ErrorModal
        isOpen={showError}
        errorMsg={errorMsg}
        handleClose={handleErrorClose}
      />

      <SuccessModal
        isOpen={showSuccess}
        message={successMsg}
        handleClose={handleSuccessClose}
      />
    </>
  );
}

export default NotificationEmailModal;
